import clsx from "clsx";
import React from "react";
import { Box, Theme, useMediaQuery, useTheme } from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import {
  ProgramTemplateCategory,
  ProgramTemplateType,
} from "@growth-machine-llc/stridist-api-client";
import { LibraryHeaderFilters } from "../header-filters/HeaderFilters";
import ResetFiltersButton from "../exercise-library/filters/ResetFiltersButton";
import {
  AllOption,
  ProgramTemplateFilterType,
} from "../../contexts/ProgramLibraryFilterContext";
import { ProgramTemplateSort } from "./filters/ProgramNameSortButton";
import ToggleIconButtonGroup from "../button/ToggleIconButtonGroup";
import { JourneyAnchors } from "../../constants";

const height = 40;

const useStyles = makeStyles<Theme>((theme) => ({
  sort: {
    backgroundColor: theme.palette.background.paper,
    fontWeight: 500,
    flexGrow: 1,
    width: "100%",
    justifyContent: "flex-end",
    [theme.breakpoints.down("sm")]: {
      justifyContent: "space-between",
    },
  },

  templateType: {
    height,
  },

  icon: {
    color: theme.palette.text.secondary,
    minWidth: "auto",

    fill: theme.palette.text.secondary,
    "&.active": {
      fill: theme.palette.primary.main,
    },

    "& svg": {
      width: 20,
      height: 20,
    },
  },

  resetFilters: {
    padding: theme.spacing(1, 2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.spacing(1),
    height: height,
  },
}));

const ProgramTemplateTypeOptions = [
  {
    value: AllOption,
    tooltip: "All templates",
    label: "All",
  },
  {
    value: ProgramTemplateType.PRIVATE,
    tooltip: "Private templates",
    label: "Private",
  },
  {
    value: ProgramTemplateType.TEAM,
    tooltip: "Team templates",
    label: "Team",
  },
  {
    value: ProgramTemplateType.PUBLIC,
    tooltip: "Public templates",
    label: "Public",
  },
];

export interface CoachProgramLibraryHeaderFiltersProps {
  query: string;
  sortKey: ProgramTemplateSort;
  onChangeQuery?: (query: string) => void;
  onChangeSortKey?: (sortKey: ProgramTemplateSort) => void;
  templateType: ProgramTemplateFilterType;
  onChangeTemplateType?: (type: ProgramTemplateFilterType) => void;
  category?: ProgramTemplateCategory[] | typeof AllOption;
  onChangeCategory?: (
    category: ProgramTemplateCategory[] | typeof AllOption,
  ) => void;
  disabledResetButton?: boolean;
  onResetFilters?: () => void;
  showResetButton?: boolean;
  defaultValues: {
    query: string;
    sortKey: ProgramTemplateSort;
    category: ProgramTemplateCategory[] | typeof AllOption;
    templateType: ProgramTemplateFilterType;
  };
  className?: string;
}

export function CoachProgramLibraryHeaderFilters(
  props: CoachProgramLibraryHeaderFiltersProps,
) {
  const {
    className,
    query,
    sortKey,
    onChangeQuery,
    onChangeSortKey,
    templateType,
    onChangeTemplateType,
    category,
    onChangeCategory,
    defaultValues: { query: defaultQuery },
    disabledResetButton,
    onResetFilters,
    showResetButton,
  } = props;

  const theme = useTheme();
  const isMdDown = useMediaQuery(theme.breakpoints.down("md"));
  const s = useStyles();

  const handleTemplateTypeChange = React.useCallback(
    (value: ProgramTemplateFilterType) => {
      onChangeTemplateType?.(value);
    },
    [onChangeTemplateType],
  );

  return (
    <>
      <LibraryHeaderFilters
        query={query}
        onChangeQuery={onChangeQuery}
        className={className}
        searchPlaceholder="Search by name"
        defaultValues={{
          query: defaultQuery,
        }}
        selectItem={
          <Box display="flex" alignItems="center" gap={2} className={s.sort}>
            <ToggleIconButtonGroup
              className={clsx(
                s.templateType,
                s.icon,
                JourneyAnchors.PROGRAM_LIBRARY_TYPE_SELECTOR,
              )}
              options={ProgramTemplateTypeOptions}
              value={templateType}
              onChange={handleTemplateTypeChange}
              displayLabel="always"
            />

            {showResetButton && (
              <ResetFiltersButton
                disabled={disabledResetButton}
                onClick={onResetFilters}
                className={clsx(
                  s.resetFilters,
                  JourneyAnchors.PROGRAM_LIBRARY_RESET_FILTERS,
                )}
              />
            )}
          </Box>
        }
      />
    </>
  );
}
